import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  X,
  Shield,
  Droplets,
  Star,
  Lightbulb,
  User,
  Accessibility,
  Baby,
  Lock,
  SprayCanIcon,
  EyeOff,
  Camera,
  Loader2,
  Trash2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 4;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

const SAFETY_LABELS = ["Very Unsafe", "Unsafe", "Neutral", "Safe", "Very Safe"];
const CLEANLINESS_LABELS = ["Very Dirty", "Dirty", "Acceptable", "Clean", "Very Clean"];

const ATTRIBUTES = [
  { id: "well_lit", label: "Well Lit", icon: Lightbulb },
  { id: "has_attendant", label: "Has Attendant", icon: User },
  { id: "wheelchair_accessible", label: "Wheelchair Accessible", icon: Accessibility },
  { id: "baby_changing", label: "Baby Changing", icon: Baby },
  { id: "working_locks", label: "Working Locks", icon: Lock },
  { id: "soap_available", label: "Soap Available", icon: SprayCanIcon },
];

export default function ReviewModal({ restroom, onClose, onSubmit }) {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [safetyRating, setSafetyRating] = useState(3);
  const [cleanlinessRating, setCleanlinessRating] = useState(3);
  const [comment, setComment] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [photos, setPhotos] = useState([]); // [{path, url, previewUrl}]
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`You can attach at most ${MAX_PHOTOS} photos`);
      return;
    }
    setUploading(true);
    for (const f of files) {
      if (f.size > MAX_PHOTO_BYTES) {
        toast.error(`${f.name} exceeds 5 MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await axios.post(`${API}/uploads/photo`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) => [
          ...prev,
          { path: r.data.path, url: r.data.url, previewUrl: URL.createObjectURL(f) },
        ]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      const next = [...prev];
      const removed = next.splice(idx, 1);
      removed.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      return next;
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl p-6 max-w-sm w-full"
        >
          <h2 className="text-xl font-bold text-slate-900 mb-2">Login Required</h2>
          <p className="text-slate-500 mb-6">Please login to leave a review.</p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-full">
              Cancel
            </Button>
            <Button onClick={() => navigate("/login")} className="flex-1 rounded-full bg-sky-600 hover:bg-sky-700">
              Login
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API}/reviews`,
        {
          restroom_id: restroom.restroom_id,
          safety_rating: safetyRating,
          cleanliness_rating: cleanlinessRating,
          attributes: selectedAttributes,
          comment: comment || null,
          photos: photos.map((p) => p.path),
          is_anonymous: isAnonymous,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Review submitted successfully!");
      onSubmit();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setLoading(false);
    }
  };

  const handleAttributeToggle = (attrId) => {
    setSelectedAttributes((prev) =>
      prev.includes(attrId) ? prev.filter((a) => a !== attrId) : [...prev, attrId]
    );
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return "bg-green-500";
    if (rating >= 3) return "bg-amber-500";
    return "bg-red-500";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto"
        data-testid="review-modal"
      >
        <div className="sticky top-0 bg-white z-10 p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">Leave a Review</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-6">
          {/* Restroom Info */}
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="font-medium text-slate-900">{restroom.name}</p>
            <p className="text-sm text-slate-500">{restroom.address}</p>
          </div>

          {/* Safety Rating */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Shield size={18} className="text-sky-600" />
              Safety Rating
            </Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => setSafetyRating(rating)}
                  className={`flex-1 py-3 rounded-xl border-2 transition-all ${
                    safetyRating >= rating
                      ? `${getRatingColor(rating)} border-transparent text-white`
                      : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                  data-testid={`safety-rating-${rating}`}
                >
                  {rating}
                </button>
              ))}
            </div>
            <p className="text-sm text-slate-500 text-center">{SAFETY_LABELS[safetyRating - 1]}</p>
          </div>

          {/* Cleanliness Rating */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Droplets size={18} className="text-sky-600" />
              Cleanliness Rating
            </Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => setCleanlinessRating(rating)}
                  className={`flex-1 py-3 rounded-xl border-2 transition-all ${
                    cleanlinessRating >= rating
                      ? `${getRatingColor(rating)} border-transparent text-white`
                      : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                  data-testid={`cleanliness-rating-${rating}`}
                >
                  {rating}
                </button>
              ))}
            </div>
            <p className="text-sm text-slate-500 text-center">{CLEANLINESS_LABELS[cleanlinessRating - 1]}</p>
          </div>

          {/* Attributes */}
          <div className="space-y-3">
            <Label>What did you notice? (Optional)</Label>
            <div className="grid grid-cols-2 gap-2">
              {ATTRIBUTES.map((attr) => {
                const Icon = attr.icon;
                return (
                  <label
                    key={attr.id}
                    className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
                      selectedAttributes.includes(attr.id)
                        ? "border-sky-500 bg-sky-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <Checkbox
                      checked={selectedAttributes.includes(attr.id)}
                      onCheckedChange={() => handleAttributeToggle(attr.id)}
                    />
                    <Icon size={14} className="text-slate-500" />
                    <span className="text-sm">{attr.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Label>Additional Comments (Optional)</Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience..."
              className="rounded-xl resize-none"
              rows={3}
              data-testid="review-comment"
            />
          </div>

          {/* Photos */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Camera size={18} className="text-sky-600" />
              Photos (up to {MAX_PHOTOS}, optional)
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200" data-testid={`review-photo-${i}`}>
                  <img src={p.previewUrl} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 shadow hover:bg-red-700"
                    aria-label="Remove photo"
                    data-testid={`review-photo-remove-${i}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-sky-500 flex items-center justify-center cursor-pointer text-slate-500 hover:text-sky-600 transition-colors" data-testid="review-photo-add">
                  {uploading ? <Loader2 className="animate-spin" size={20} /> : <Camera size={20} />}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleFiles}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Anonymous Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-2">
              <EyeOff size={18} className="text-slate-500" />
              <div>
                <p className="font-medium text-sm">Post Anonymously</p>
                <p className="text-xs text-slate-500">Your name won't be shown</p>
              </div>
            </div>
            <Switch
              checked={isAnonymous}
              onCheckedChange={setIsAnonymous}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold"
            data-testid="submit-review-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Submitting...
              </span>
            ) : (
              "Submit Review"
            )}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
